// İş Ortağı Portalı ortak arayüz parçaları — tasarım teslimi vertex-partner-handoff 1.0.
// Sınıflar `styles/partner-portal.css`'ten (.pp kökü altında). Sayfalar bu parçaları
// kullanır; iç VERTEX bileşenleri (PageWrapper, KPI) partner sayfalarında kullanılmaz.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Loader2, ChevronDown, Download } from 'lucide-react'
import { partnerService } from '../../../services/partnerService'
import DataTable from '../../common/DataTable'
import { useAuthStore } from '../../../stores/authStore'

// Sütun bazlı görüntüleme izinleri — anahtarlar `partner_service.PARTNER_PAGE_GROUP` columns ile AYNI.
// Satır yoksa sütun AÇIK (authStore.hasPermission partner kod varsayılanı); şirket/kişi "Yok" derse çizilmez.
const PARTNER_COLS = {
  CATALOG: { brand: 'BRAND', matgrp_text: 'MATGRP', family: 'FAMILY', lifecycle: 'LIFECYCLE',
             stock_band: 'STOCK', list_price: 'LIST_PRICE', discount_pct: 'DISCOUNT', unit_price: 'NET_PRICE' },
  ORDERS: { your_ref: 'YOUR_REF', date: 'DATE', total: 'TOTAL', stage_label: 'STAGE', _progress: 'PROGRESS' },
  DOCS: { date: 'DATE', einvoice_no: 'EINVOICE', customer_po: 'CUSTOMER_PO', _steps: 'STEPS', currency: 'CURRENCY', total: 'TOTAL' },
  STOCK: { partner_sku: 'SKU', qty: 'QTY', uom: 'UOM', price: 'PRICE' },
}

/** İzinli sütunlar: `useColumnAccess('CATALOG')` → { hidden:Set, filter(cols) } */
export function useColumnAccess(table) {
  const perms = useAuthStore((s) => s.user?.permissions)
  const hasPermission = useAuthStore((s) => s.hasPermission)
  return useMemo(() => {
    const map = PARTNER_COLS[table] || {}
    const hidden = new Set(Object.entries(map)
      .filter(([, key]) => !hasPermission(`PARTNER.COL.${table}.${key}`, 'View', 'Own'))
      .map(([acc]) => acc))
    return { hidden, filter: (cols) => cols.filter((c) => !hidden.has(c.accessor)) }
  }, [table, perms, hasPermission]) // eslint-disable-line react-hooks/exhaustive-deps
}

export const ICON = { size: 16, strokeWidth: 2 }

/** Panel özeti (şirket, sepet, talep sayaçları, temsilci) — kabuk ve sayfalar paylaşır. */
export function usePartnerMe() {
  return useQuery({
    queryKey: ['partner-me'],
    queryFn: () => partnerService.me(),
    staleTime: 60 * 1000,
  })
}

export function PPage({ title, subtitle, actions, children, headerStyle }) {
  return (
    <>
      <header className="page-header" style={headerStyle}>
        <div className="page-header__text">
          <h1 className="page-title">{title}</h1>
          {subtitle && <p className="page-subtitle">{subtitle}</p>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </header>
      {children}
    </>
  )
}

export function PBadge({ tone = 'neutral', dot = false, children, title }) {
  return (
    <span className={`badge badge--${tone}`} title={title}>
      {dot && <span className="badge__dot" />}
      {children}
    </span>
  )
}

/** 4 parçalı ilerleme çubuğu (Talep → Onay → Sipariş → Fatura). `done` 0–4. */
export function PProgress({ done = 0, total = 4, success = false, label }) {
  return (
    <div className={`progress ${success ? 'progress--success' : ''}`} role="img" aria-label={label}>
      {Array.from({ length: total }).map((_, i) => <span key={i} className={i < done ? 'is-done' : ''} />)}
    </div>
  )
}

export function PEmpty({ icon: Icon, title, text, action }) {
  return (
    <div className="empty">
      {Icon && <span className="icon-box"><Icon {...ICON} size={20} /></span>}
      <strong>{title}</strong>
      {text && <span>{text}</span>}
      {action}
    </div>
  )
}

/** Tablo iskeleti — 8 satır (handoff §8). 10 sn'yi aşarsa "Hâlâ yükleniyor…". */
export function PSkeletonRows({ cols = 5, rows = 8 }) {
  const [slow, setSlow] = useState(false)
  useEffect(() => { const t = setTimeout(() => setSlow(true), 10000); return () => clearTimeout(t) }, [])
  return (
    <>
      <div className="table-wrap">
        <table className="table">
          <tbody>
            {Array.from({ length: rows }).map((_, r) => (
              <tr key={r}>
                {Array.from({ length: cols }).map((__, c) => (
                  <td key={c}><span className="skeleton" style={{ width: `${50 + ((r * 7 + c * 13) % 45)}%` }} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {slow && <div className="card__foot"><span>Hâlâ yükleniyor…</span></div>}
    </>
  )
}

export function PError({ onRetry, text = 'Liste yüklenemedi' }) {
  return (
    <div className="card--pad">
      <div className="callout callout--error" role="alert" style={{ alignItems: 'center' }}>
        <span style={{ flexGrow: 1 }}>{text}</span>
        {onRetry && <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>Tekrar dene</button>}
      </div>
    </div>
  )
}

export function PSearch({ value, onChange, placeholder, outline = false, style, onEnter, label = 'Ara' }) {
  return (
    <label className={`search ${outline ? 'search--outline' : ''}`} style={style}>
      <Search {...ICON} />
      <span className="sr-only">{label}</span>
      <input type="search" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) onEnter(e.currentTarget.value) }} />
    </label>
  )
}

/** ARIA tablist — ←/→ ile sekme değişir (handoff §10). */
export function PTabs({ tabs, value, onChange, soft = false, label }) {
  const onKey = (e, i) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    const n = tabs.length
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : n - 1)) % n]
    onChange(next.value)
    e.currentTarget.parentElement?.children?.[(i + (e.key === 'ArrowRight' ? 1 : n - 1)) % n]?.focus()
  }
  return (
    <div className={`tabs ${soft ? 'tabs--soft' : ''}`} role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button key={t.value} type="button" className="tab" role="tab"
                aria-selected={value === t.value} tabIndex={value === t.value ? 0 : -1}
                onKeyDown={(e) => onKey(e, i)} onClick={() => onChange(t.value)}>
          {t.label}{t.count != null ? ` · ${t.count}` : ''}
        </button>
      ))}
    </div>
  )
}

/** İstemci sayfalaması — liste zaten tek istekte geliyor. */
export function usePaged(items, pageSize = 20, deps = []) {
  const [page, setPage] = useState(1)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1) }, deps)
  const pages = Math.max(1, Math.ceil((items?.length || 0) / pageSize))
  const cur = Math.min(page, pages)
  const slice = useMemo(() => (items || []).slice((cur - 1) * pageSize, cur * pageSize), [items, cur, pageSize])
  return { page: cur, pages, setPage, slice }
}

export function PPagination({ page, pages, setPage, total, unit = 'kayıt' }) {
  if (pages <= 1) return total != null ? <div className="card__foot"><span>{total} {unit}</span></div> : null
  const around = []
  for (let p = Math.max(1, page - 2); p <= Math.min(pages, page + 2); p++) around.push(p)
  return (
    <div className="card__foot">
      <span>Sayfa {page} / {pages}{total != null ? ` · ${total} ${unit}` : ''}</span>
      <nav className="pagination" aria-label="Sayfalama">
        {page > 1 && <button type="button" className="page-btn" onClick={() => setPage(page - 1)}>← Önceki</button>}
        {around.map((p) => (
          <button key={p} type="button" className="page-btn" aria-current={p === page ? 'page' : undefined} onClick={() => setPage(p)}>{p}</button>
        ))}
        {page < pages && <button type="button" className="page-btn" onClick={() => setPage(page + 1)}>Sonraki →</button>}
      </nav>
    </div>
  )
}

export function PSpin({ size = 16 }) {
  return <Loader2 size={size} className="spin" aria-hidden="true" />
}

/** Kişi adından baş harfler (Türkçe büyük harf). */
export function initialsOf(name, fallback = 'U') {
  const s = String(name || '').trim()
  if (!s) return fallback.slice(0, 2).toLocaleUpperCase('tr')
  return s.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toLocaleUpperCase('tr')
}

/** Uzun firma adının kısa hâli — kullanıcı kartı için ilk kelime (Title Case). */
export function shortCompany(name) {
  const w = String(name || '').trim().split(/\s+/)[0] || ''
  return w ? w.charAt(0).toLocaleUpperCase('tr') + w.slice(1).toLocaleLowerCase('tr') : ''
}

// ── Sipariş durumu → rozet tonu + 4 parçalı ilerleme (Talep → Onay → Sipariş → Fatura) ──
// Kaynak sunucudaki `partner_service.TRACK_STAGES`; burada yalnız görsel eşleme.
export const STAGE_TONE = {
  requested: 'warning', approved: 'brand', rejected: 'danger', cancelled: 'neutral',
  order_pending: 'warning', ordered: 'info', delivered: 'info', invoiced: 'success',
}
// Sipariş takibinin dört adımı — satırdaki `steps` (sunucu, belge zincirinden) esastır;
// yoksa (ana sayfa son siparişleri) aşamadan türetilir.
export const TRACK_STEPS = [
  { key: 'approved', label: 'Onay' },
  { key: 'ordered', label: 'Sipariş' },
  { key: 'delivered', label: 'Sevk' },
  { key: 'invoiced', label: 'Fatura' },
]
const STAGE_STEP_COUNT = { requested: 0, approved: 1, order_pending: 2, ordered: 2, delivered: 3, invoiced: 4 }
const STOPPED_STAGES = { rejected: 'Reddedildi', cancelled: 'Geri çekildi' }

export function trackState(row) {
  const stage = row?.stage
  if (STOPPED_STAGES[stage]) return { done: {}, count: 0, stopped: true, next: null, label: STOPPED_STAGES[stage] }
  let done = {}
  if (row?.steps) {
    TRACK_STEPS.forEach((s) => { done[s.key] = !!row.steps[s.key] })
  } else {
    const n = STAGE_STEP_COUNT[stage] ?? 0
    TRACK_STEPS.forEach((s, i) => { done[s.key] = i < n })
  }
  // Sonraki adım tamamlanmışsa öncekiler de tamam sayılır (ör. faturası kesilmiş ama irsaliyesiz belge)
  let seen = false
  for (let i = TRACK_STEPS.length - 1; i >= 0; i -= 1) {
    if (done[TRACK_STEPS[i].key]) seen = true
    else if (seen) done[TRACK_STEPS[i].key] = true
  }
  const next = TRACK_STEPS.find((s) => !done[s.key]) || null
  const count = TRACK_STEPS.filter((s) => done[s.key]).length
  return { done, count, stopped: false, next, label: next ? `${next.label} bekleniyor` : 'Tamamlandı' }
}

/** İlerleme: dört etiketli adım — tamam (yeşil ✓) · sıradaki (amber) · sonraki (gri). */
export function StageProgress({ stage, row }) {
  const st = trackState(row || { stage })
  if (st.stopped) {
    return <span style={{ fontSize: 12, color: 'var(--c-danger-600, #dc2626)', fontWeight: 600 }}>{st.label}</span>
  }
  return (
    <div role="img" aria-label={`${st.count}/4 adım tamam · ${st.label}`} title={st.label}
         style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
      {TRACK_STEPS.map((s, i) => {
        const isDone = st.done[s.key]
        const isNext = st.next && st.next.key === s.key
        const color = isDone ? '#047857' : isNext ? '#b45309' : '#9ca3af'
        const bg = isDone ? '#ecfdf5' : isNext ? '#fffbeb' : '#f9fafb'
        const border = isDone ? '#a7f3d0' : isNext ? '#fcd34d' : '#e5e7eb'
        return (
          <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {i > 0 && <span style={{ width: 6, height: 1, background: isDone ? '#6ee7b7' : '#e5e7eb' }} />}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 999,
              fontSize: 11, fontWeight: isNext ? 600 : 500, lineHeight: '16px', whiteSpace: 'nowrap',
              color, background: bg, border: `1px ${isNext ? 'dashed' : 'solid'} ${border}`,
            }}>
              {isDone ? '✓' : isNext ? '…' : '○'} {s.label}
            </span>
          </span>
        )
      })}
    </div>
  )
}

/** Tablo sütunu süzgeci: satırın sıradaki adımı ("Sevk bekleniyor", "Tamamlandı"…). */
export const trackFilterValue = (row) => trackState(row).label

// Panel talebi durumu (rpt_partner_order.status) → ton
export const REQUEST_TONE = { submitted: 'warning', approved: 'success', rejected: 'danger', cancelled: 'neutral', cart: 'neutral' }

/** "Etiket: değer" filtre düğmesi + açılır seçim listesi (handoff §5 filter-btn). */
export function PFilter({ label, valueLabel, options = [], value, onChange, disabled, disabledTitle, allLabel = 'Tümü' }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="filter-btn" aria-haspopup="listbox" aria-expanded={open}
              disabled={disabled} title={disabled ? disabledTitle : undefined}
              style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              onClick={() => setOpen((o) => !o)}>
        <span>{label}</span><strong>{valueLabel || allLabel}</strong><ChevronDown size={16} strokeWidth={2} />
      </button>
      {open && (
        <div className="listbox" role="listbox" aria-label={label}>
          <button type="button" role="option" aria-selected={!value} onClick={() => { onChange(''); setOpen(false) }}>{allLabel}</button>
          {options.map((o) => (
            <button key={o.value} type="button" role="option" aria-selected={value === o.value}
                    onClick={() => { onChange(o.value); setOpen(false) }}>
              <span>{o.label}</span>{o.count != null && <small>{o.count}</small>}
            </button>
          ))}
          {!options.length && <div className="muted" style={{ padding: '8px 10px' }}>Seçenek yok</div>}
        </div>
      )}
    </div>
  )
}

/** Adet seçici — en az 1; ↑/↓ ile değişir (handoff §10). */
export function PStepper({ value, onChange, large = false, small = false, label = 'Adet', min = 1 }) {
  const v = Number(value) || min
  return (
    <div className={`stepper ${large ? 'stepper--lg' : ''} ${small ? 'stepper--sm' : ''}`} onClick={(e) => e.stopPropagation()}>
      <button type="button" aria-label="Azalt" onClick={() => onChange(Math.max(min, v - 1))}>−</button>
      <input type="number" min={min} value={value} aria-label={label}
             onChange={(e) => onChange(Math.max(min, Number(e.target.value) || min))}
             onKeyDown={(e) => {
               if (e.key === 'ArrowUp') { e.preventDefault(); onChange(v + 1) }
               if (e.key === 'ArrowDown') { e.preventDefault(); onChange(Math.max(min, v - 1)) }
             }} />
      <button type="button" aria-label="Artır" onClick={() => onChange(v + 1)}>+</button>
    </div>
  )
}

/** Stok — yalnız SAYI (kullanıcı kararı 16.10.2026: "Az (1)" / "8 adet" yazısı yok).
 *  Şirket bant modundaysa (`show_exact_stock=0`) sayı yanıtta YOKTUR → bant yazısı kalır. */
export function StockBadge({ qty, band, total, reserved }) {
  if (qty != null) {
    const n = Number(qty) || 0
    const num = (v) => (Number(v) || 0).toLocaleString('tr-TR')
    // Anlık stok gelmişse (tam adet modu): etiketli TEK satır — Kull. · Rez. · Top. her biri sabit
    // genişlikte sağa hizalı hücrede, satırlar alt alta hizalı okunur (kullanıcı tasarımı 21.10.2026)
    if (total != null) {
      const r = Number(reserved) || 0
      const cell = (label, value, color, labelColor) => (
        <span style={{ display: 'inline-flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 4, width: 64 }}>
          <span style={{ fontSize: 11, color: labelColor || 'var(--c-subtle)' }}>{label}</span>
          <strong style={{ color, fontVariantNumeric: 'tabular-nums' }}>{num(value)}</strong>
        </span>
      )
      return (
        <span style={{ display: 'inline-flex', alignItems: 'baseline', whiteSpace: 'nowrap' }}
              title={`Kullanılabilir ${num(qty)} · Rezerve ${num(reserved)} · Toplam ${num(total)}`}>
          {cell('Kull.', qty, n > 0 ? '#15803D' : 'var(--c-subtle)')}
          {cell('Rez.', reserved, r > 0 ? '#B45309' : '#C4C9D2', r > 0 ? '#B45309' : '#C4C9D2')}
          {cell('Top.', total, 'var(--c-text-2)')}
        </span>
      )
    }
    return (
      <strong style={{ fontVariantNumeric: 'tabular-nums', color: n > 0 ? '#15803D' : 'var(--c-subtle)' }}>
        {n.toLocaleString('tr-TR')}
      </strong>
    )
  }
  if (band === 'in') return <PBadge tone="success" dot>Stokta</PBadge>
  if (band === 'low') return <PBadge tone="warning" dot>Az</PBadge>
  return <PBadge tone="neutral" dot>Stokta yok</PBadge>
}

/**
 * Portalın TEK tablo yapısı (kullanıcı kararı 16.10.2026: "bütün panellerdeki tablolar aynı
 * yapıda") — iç VERTEX DataTable'ı: her sütunda huni, başlıktan sıralama, Sütunlar/Filtreler,
 * ekrana sığan sabit düzen (fixedLayout + compact), sıkı satır, kendi sayfalaması (50).
 * Katalog sunucu tarafı huni/sıralama ile aynı bileşeni doğrudan kullanır.
 */
export function PTable({ height = 'calc(100vh - 300px)', ...props }) {
  return (
    <DataTable dense compact fixedLayout fitWidth itemsPerPage={50} maxHeight={height}
               className="border-0 rounded-none" {...props} />
  )
}

/** Tablo seçim çubuğu — kutucukla seçilen satırlar için (headerLeft'e konur) */
export function PSelectionBar({ n, unit, onExport, onClear }) {
  if (!n) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <strong style={{ fontSize: 13 }}>{n.toLocaleString('tr-TR')} {unit} seçildi</strong>
      {onExport && (
        <button type="button" className="btn btn--primary btn--sm" onClick={onExport}>
          <Download size={14} strokeWidth={1.75} />Seçilenleri indir
        </button>
      )}
      <button type="button" className="btn btn--ghost btn--sm" onClick={onClear}>Seçimi temizle</button>
    </div>
  )
}

/** Tablo araç satırındaki adet yazısı */
export function PCount({ n, unit }) {
  return (
    <span className="muted" style={{ fontSize: 12 }}>
      <strong style={{ color: 'var(--c-text)' }}>{Number(n || 0).toLocaleString('tr-TR')}</strong> {unit}
    </span>
  )
}
